import {
  Component,
  HostListener,
  OnInit,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DateTime } from 'luxon';

import {
  CancelButtonComponent,
  CloseIconButtonComponent,
  SubmitButtonComponent,
} from '@sandbox-platform/shared-ui';

import {
  CommissionPaymentMethod,
  CreateCommissionPaymentDTO,
  PAYMENT_METHOD_LABELS,
} from '../../../../types/commission-payment.types';

@Component({
  standalone: true,
  imports: [
    DecimalPipe,
    ReactiveFormsModule,
    CancelButtonComponent,
    CloseIconButtonComponent,
    SubmitButtonComponent,
  ],
  selector: 'lib-add-payment-modal',
  templateUrl: './add-payment-modal.component.html',
})
export class AddPaymentModalComponent implements OnInit {
  invoiceId = input.required<string>();
  currency = input<string>('KES');
  remainingAmount = input<number>(0);

  protected readonly remainingAmountMajor = computed(() => this.remainingAmount() / 100);

  paymentSubmit = output<CreateCommissionPaymentDTO>();
  closed = output<void>();

  protected readonly isOpen = signal(false);
  protected readonly submitted = signal(false);
  protected form!: FormGroup;

  protected readonly paymentMethods: { value: CommissionPaymentMethod; label: string }[] =
    Object.entries(PAYMENT_METHOD_LABELS).map(([value, label]) => ({
      value: value as CommissionPaymentMethod,
      label,
    }));

  constructor(private readonly fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      amount: [null, [Validators.required, Validators.min(0.01)]],
      payment_date: [
        DateTime.now().toFormat('yyyy-MM-dd'),
        Validators.required,
      ],
      payment_method: ['bank_transfer', Validators.required],
      payment_reference: ['', Validators.required],
      notes: [''],
    });
  }

  open(): void {
    this.submitted.set(false);
    const amountControl = this.form.get('amount')!;
    amountControl.setValidators([
      Validators.required,
      Validators.min(0.01),
      Validators.max(this.remainingAmountMajor()),
    ]);
    this.form.reset({
      amount: null,
      payment_date: DateTime.now().toFormat('yyyy-MM-dd'),
      payment_method: 'bank_transfer',
      payment_reference: '',
      notes: '',
    });
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen()) {
      this.close();
    }
  }

  protected onSubmit(): void {
    this.submitted.set(true);
    if (this.form.invalid) return;

    const raw = this.form.value;
    const dto: CreateCommissionPaymentDTO = {
      invoice_id: this.invoiceId(),
      amount_minor: Math.round(raw.amount * 100),
      currency: this.currency(),
      payment_date: DateTime.fromISO(raw.payment_date, { zone: 'utc' }).toISO()!,
      payment_method: raw.payment_method,
      payment_reference: raw.payment_reference,
      notes: raw.notes || undefined,
    };

    this.paymentSubmit.emit(dto);
    this.close();
  }
}
